import { Formik } from "formik";
import { formatSubcontractorName } from "../../lib/Misc";
import { useCallback, useEffect, useState } from "react";
import Spin from "../../components/Spin";
import {
  API,
  fileUpload,
  get,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import CustomLabelText from "../../components/CustomLabelText";
import UserSelect from "../../components/UserSelect";
import { Loader2 } from "lucide-react";
import { useOrganization } from "../../lib/context/OrganisationContext";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import MainBody from "../../components/MainBody/MainBody";
import { Button } from "../../components/ui/button";

export default function UserPermission({
  expandList = false,
  userId = "",
  userData,
  handleSucess
}) {
  const [userLoading, setUserLoading] = useState(false);
  const [userListing, setUserListing] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);

  const { organizationData } = useOrganization();

  const handlePermissionChange = (setFieldValue, values, moduleKey, type) => {
    const updated = { ...values.permissions[moduleKey] };

    // if (type === "all") {
    //   const newVal = !updated.all;
    //   updated.all = newVal;
    //   updated.read = newVal;
    //   updated.write = newVal;
    // } else {
    updated[type] = !updated[type];
    //   updated.all = updated.read && updated.write;
    // }

    setFieldValue(`permissions.${moduleKey}`, updated);
  };

  useEffect(() => {
    if (userList) {
      let userTempListing = userList?.map((item) => ({
        ...item,
        value: item?.id,
        label: formatSubcontractorName(item),
      }));
      setUserListing(userTempListing || []);
    } else {
      setUserListing([]);
    }
  }, [userList]);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await get(
        `${API.GET_MODULE_LIST}?limit=${-1}&page=${1}`
      );

      setLoading(false);
      const contractorsList = data.data.data;
      setModules(contractorsList);
      return data.data;
    } catch (error) {
      setLoading(false);
      setModules([]);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();
    // Append user_id if needed
    formData.append("user_id", value.user_id);

    // Convert permissions object to required format
    const permissions = value.permissions;
    const modules = Object.entries(permissions);

    modules.forEach(([slug, perms], index) => {
      formData.append(`permissions[${index}][slug]`, slug);
      formData.append(`permissions[${index}][read]`, perms.read ? true : false);
      formData.append(
        `permissions[${index}][write]`,
        perms.write ? true : false
      );
    });

    try {
      setLoading(true);
      await fileUpload(`${API.USER_PERMISSION}`, formData);
      handleSucess && handleSucess();
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <MainBody
      search={false}
      expandList={expandList}
      className={expandList ? "p-0 !bg-transparent" : ""}
    >
      <Formik
        initialValues={{
          user_id: userId ? userId : "",
          permissions: userData?.permissions ? userData?.permissions : {},
        }}
        enableReinitialize
        onSubmit={handleFormSubmit}
        validateOnBlur={true}
        validateOnChange={true}
        //   validationSchema={AddOrganizationSchema}
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isValid,
        }) => (
          <>
            {loading && <Spin fullscreen={true}/>}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="entry-section md:m-0 m-5"
            >
              {/* User Selection */}
              <div className="grid gap-2 max-w-80">
                <Label htmlFor="user">
                  <CustomLabelText
                    required={false}
                    labelText="User"
                    errorField={errors?.user_id}
                  />
                </Label>
                <div className="relative mt-1">
                  <UserSelect
                    orgId={organizationData?.organizationId}
                    frameworkData={userListing} // List of vendors
                    optionType="User"
                    handleSelect={(e) => {
                      setFieldValue(`user_id`, e);
                    }}
                    name="user_id"
                    disabled={false}
                    initialValue={values?.user_id ? values?.user_id : ""}
                    setUserList={setUserList} // Update vendor list as new data loads
                    setUserLoading={setUserLoading} // Control loading state
                    onClear={() => {
                      setFieldValue(`user_id`, "");
                    }}
                    className={
                      errors?.user_id &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                    loading={userLoading}
                  />
                  {userLoading && (
                    <span className="absolute top-2 right-2 animate-spin">
                      <Loader2 width={20} height={20} />
                    </span>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="overflow-auto border rounded-lg mt-6 max-h-[50vh]">
                <div
                  className={`grid grid-cols-3 text-sm font-medium border-b ${TRClasses}`}
                >
                  <div className={`${THClasses} ${TDClasses}`}>Module</div>
                  {/* <div className={`text-center ${THClasses} ${TDClasses}`}>
                    All
                  </div> */}
                  <div className={`text-center ${THClasses} ${TDClasses}`}>
                    Read
                  </div>
                  <div className={`text-center ${THClasses} ${TDClasses}`}>
                    Write
                  </div>
                </div>

                {modules.map(({ name, slug }) => (
                  <div
                    key={slug}
                    className={`grid grid-cols-3 border-b items-center text-sm ${TRClasses}`}
                  >
                    {/* Module Name */}
                    <div className={`font-medium pt-2 pb-3 ${TDClasses}`}>
                      {name}
                    </div>

                    {/* All / Read / Write Checkboxes */}
                    {["read", "write"].map((perm) => (
                      <div
                        key={perm}
                        className={`flex pt-2 pb-3 justify-center ${TDClasses}`}
                      >
                        <Checkbox
                          id={`${slug}_${perm}`}
                          checked={values?.permissions?.[slug]?.[perm]}
                          onCheckedChange={() =>
                            handlePermissionChange(
                              setFieldValue,
                              values,
                              slug,
                              perm
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
                <Button type="submit" disabled={!isValid}>
                  Save
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </MainBody>
  );
}
